// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import {
  intelligenceAdminApi,
  type ChatUseCaseDefault,
  type ModelDTO,
  type ProviderDTO,
  type UpdateUseCaseDefaultRequest,
  type VoiceProfile,
} from '../../api/intelligenceAdminApi';
import VoiceProfileEditor from './VoiceProfileEditor';

interface Props {
  useCase: ChatUseCaseDefault;
  onSaved: (next: ChatUseCaseDefault) => void;
}

const DefaultsForm: React.FC<Props> = ({ useCase, onSaved }) => {
  const [draft, setDraft] = useState<ChatUseCaseDefault>(useCase);
  const [providers, setProviders] = useState<ProviderDTO[]>([]);
  const [models, setModels] = useState<ModelDTO[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraft(useCase); }, [useCase]);

  useEffect(() => {
    intelligenceAdminApi.listProviders()
      .then(setProviders)
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load providers'));
  }, []);

  useEffect(() => {
    if (!draft.defaultLlmProvider) return;
    intelligenceAdminApi.listModels(draft.defaultLlmProvider)
      .then(setModels)
      .catch(() => setModels([]));
  }, [draft.defaultLlmProvider]);

  const update = (patch: Partial<ChatUseCaseDefault>) =>
    setDraft(prev => ({ ...prev, ...patch }));

  const onVoiceChange = (next: { enabled: boolean; profile: VoiceProfile }) =>
    update({ voiceEnabled: next.enabled, voiceProfile: next.profile });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const body: UpdateUseCaseDefaultRequest = {
        displayName: draft.displayName,
        description: draft.description,
        defaultSystemPrompt: draft.defaultSystemPrompt,
        defaultLlmProvider: draft.defaultLlmProvider,
        defaultModel: draft.defaultModel,
        defaultTemperature: draft.defaultTemperature,
        defaultMaxTokens: draft.defaultMaxTokens,
        maxMessagesPerSession: draft.maxMessagesPerSession,
        enabled: draft.enabled,
        agentName: draft.agentName,
        personality: draft.personality,
        voiceEnabled: draft.voiceEnabled,
        voiceProfile: draft.voiceProfile,
      };
      const saved = await intelligenceAdminApi.updateUseCase(draft.code, body);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="defaults-form">
      <div style={section}>
        <h3>Use case</h3>
        <label>Display name
          <input type="text" value={draft.displayName ?? ''}
            onChange={(e) => update({ displayName: e.target.value })} style={input}/>
        </label>
        <label>Description
          <textarea value={draft.description ?? ''}
            onChange={(e) => update({ description: e.target.value })} style={input} rows={2}/>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <input type="checkbox" checked={draft.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            data-testid="enabled-toggle"/>
          Enabled (uncheck to globally disable this use case)
        </label>
      </div>

      <div style={section}>
        <h3>Persona</h3>
        <label>Agent name
          <input type="text" maxLength={60} value={draft.agentName ?? ''}
            onChange={(e) => update({ agentName: e.target.value })}
            style={input} data-testid="agent-name"/>
        </label>
        <label>Personality
          <textarea value={draft.personality ?? ''} maxLength={500}
            onChange={(e) => update({ personality: e.target.value })}
            placeholder="e.g. Warm, plain-English, never patronising"
            style={input} rows={2} data-testid="personality"/>
        </label>
      </div>

      <div style={section}>
        <h3>LLM</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label>Provider
            <select value={draft.defaultLlmProvider}
              onChange={(e) => update({ defaultLlmProvider: e.target.value })}
              style={input} data-testid="provider-select">
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.display}{p.platformOnly ? ' (platform-only)' : ''}</option>
              ))}
            </select>
          </label>
          <label>Model
            <select value={draft.defaultModel}
              onChange={(e) => update({ defaultModel: e.target.value })}
              style={input} data-testid="model-select">
              {models.map(m => <option key={m.id} value={m.id}>{m.display}</option>)}
              {!models.find(m => m.id === draft.defaultModel) && draft.defaultModel
                && <option value={draft.defaultModel}>{draft.defaultModel} (custom)</option>}
            </select>
          </label>
        </div>
      </div>

      <div style={section}>
        <h3>System prompt</h3>
        <p style={{ color: '#6b7280', fontSize: 13, marginTop: 0 }}>
          Variables: <code>{'{{agent_name}}'}</code>, <code>{'{{personality}}'}</code>,
          <code> {'{{schema_summary}}'}</code> are substituted at session-create time.
        </p>
        <textarea value={draft.defaultSystemPrompt ?? ''} rows={10}
          onChange={(e) => update({ defaultSystemPrompt: e.target.value })}
          style={{ ...input, fontFamily: 'monospace' }}
          data-testid="system-prompt"/>
      </div>

      <VoiceProfileEditor
        enabled={draft.voiceEnabled ?? false}
        profile={draft.voiceProfile}
        agentName={draft.agentName}
        onChange={onVoiceChange}/>

      <p style={{ marginTop: 16, fontSize: 13, color: '#6b7280' }}>
        The fact-find question set is managed separately under{' '}
        <strong>Intelligence → Profile Form Schemas</strong>.
      </p>

      {error && <div style={{ color: '#b91c1c', marginTop: 12 }} data-testid="defaults-error">{error}</div>}

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button type="submit" disabled={submitting} data-testid="defaults-save"
          style={{ background: '#1e40af', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 4 }}>
          {submitting ? 'Saving…' : 'Save defaults'}
        </button>
        <button type="button" onClick={() => setDraft(useCase)}>Reset</button>
      </div>
    </form>
  );
};

const section: React.CSSProperties = { marginTop: 24 };
const input: React.CSSProperties = {
  display: 'block', width: '100%', marginTop: 4,
  border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px',
};

export default DefaultsForm;
